import { movieList, tvList, featuredIds } from '@/mocks/videos';
import type { Episode, VideoItem, VideoType } from '@/types/video';

export const movies: VideoItem[] = movieList.map((item) => ({
  ...item,
  type: 'movie' as VideoType,
}));

export const tvShows: VideoItem[] = tvList.map((item) => ({
  ...item,
  type: 'tv' as VideoType,
}));

export const allVideos: VideoItem[] = [...movies, ...tvShows];

export const PAGE_SIZE = 8;

export const regionOptions = ['全部', '大陆', '香港', '欧美', '日韩'];

export const yearOptions = [
  { label: '全部年份', value: 'all' },
  { label: '近5年', value: 'recent' },
  { label: '2015-2020', value: '2015-2020' },
  { label: '更早', value: 'earlier' },
];

export const sortOptions = [
  { label: '按热度', value: 'popularity' },
  { label: '按评分', value: 'rating' },
  { label: '按更新时间', value: 'updatedAt' },
];

export function getVideosByType(type: VideoType): VideoItem[] {
  return type === 'movie' ? movies : tvShows;
}

export function getVideoById(type: VideoType, id: string): VideoItem | undefined {
  return getVideosByType(type).find((item) => item.id === id);
}

export function getFeatured(): VideoItem[] {
  return featuredIds
    .map((id) => allVideos.find((item) => item.id === id))
    .filter((item): item is VideoItem => Boolean(item));
}

export function detailPath(video: VideoItem): string {
  return `/detail/${video.type}/${video.id}`;
}

export function categoryPath(type: VideoType): string {
  return `/category/${type}`;
}

export function typeLabel(type: VideoType): string {
  return type === 'movie' ? '电影' : '电视剧';
}

export function matchYear(year: number, range: string): boolean {
  const currentYear = 2025;
  if (range === 'recent') return year >= currentYear - 4;
  if (range === '2015-2020') return year >= 2015 && year <= 2020;
  if (range === 'earlier') return year < 2015;
  return true;
}

export function filterVideos(
  list: VideoItem[],
  region: string,
  yearRange: string,
  sortBy: string,
): VideoItem[] {
  const filtered = list.filter((item) => {
    const regionMatch = region === '全部' || item.regions.includes(region);
    return regionMatch && matchYear(item.year, yearRange);
  });

  return [...filtered].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'updatedAt') return a.updatedAt < b.updatedAt ? 1 : -1;
    return b.popularity - a.popularity;
  });
}

const chapterWords = ['起点', '旧信', '回声', '暗巷', '边界', '倒影', '断点', '余温', '长夜', '黎明', '来路', '归途'];

export function getEpisodes(video: VideoItem): Episode[] {
  const total = video.episodeCount ?? 0;
  return Array.from({ length: total }, (_, index) => ({
    id: `${video.id}-ep-${index + 1}`,
    number: index + 1,
    title: `第${index + 1}集 · ${chapterWords[index % chapterWords.length]}`,
    duration: index % 5 === 4 ? '51分钟' : index % 3 === 0 ? '46分钟' : '44分钟',
  }));
}

export function getRelated(video: VideoItem, limit = 6): VideoItem[] {
  const pool = getVideosByType(video.type).filter((item) => item.id !== video.id);
  const scored = pool.map((item) => {
    const genreScore = item.genres.filter((g) => video.genres.includes(g)).length * 3;
    const tagScore = item.tags.filter((t) => video.tags.includes(t)).length * 2;
    const regionScore = item.regions.some((r) => video.regions.includes(r)) ? 1 : 0;
    return { item, score: genreScore + tagScore + regionScore };
  });

  scored.sort((a, b) => b.score - a.score || b.item.rating - a.item.rating);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function searchVideos(keyword: string): VideoItem[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return allVideos.filter((item) => {
    const haystack = [
      item.title,
      item.director,
      ...item.cast,
      ...item.genres,
      ...item.tags,
      item.synopsis,
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(query);
  });
}

export function latestUpdates(limit = 6): VideoItem[] {
  return [...allVideos]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, limit);
}